import { useCallback, useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { api } from '@/api/client';

export interface CaptchaValue { captchaToken: string; captchaAnswer: string }

/**
 * The security code on the portal login.
 *
 * Served by our own API (`GET /auth/captcha`) rather than reCAPTCHA or Turnstile — no third-party
 * account, no key pair only the business can create, and nobody logging in is reported to anyone.
 *
 * It is required for the WEB portals only; the server decides that per request from the `X-App`
 * header this bundle sends, so the mobile app and API integrations are untouched.
 *
 * `reloadKey` is bumped by the page after a failed sign-in. That matters: a code is single-use on
 * the server, so after any rejected attempt the one on screen is already dead and leaving it there
 * would make the next, correct attempt fail too.
 */
export function LoginCaptcha({
  value,
  onChange,
  reloadKey = 0,
}: {
  value: CaptchaValue;
  onChange: (v: CaptchaValue) => void;
  reloadKey?: number;
}) {
  const [image, setImage] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      const r = await api.get('/auth/captcha');
      setImage(r.data.image);
      onChange({ captchaToken: r.data.token, captchaAnswer: '' });
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
    // `onChange` is a fresh function on every parent render; depending on it would refetch forever.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { void load(); }, [load, reloadKey]);

  return (
    <div>
      <label htmlFor="login-captcha" className="block text-sm font-medium text-slate-700 mb-1">
        Security code
      </label>

      <div className="flex items-stretch gap-2">
        <div className="relative grid h-[42px] w-[150px] shrink-0 place-items-center overflow-hidden rounded-lg border border-slate-300 bg-[#F5F6FF]">
          {image && !loading && (
            // An <img>, never text in the DOM, so nothing on the page can read the code.
            <img src={image} alt="" className="h-full w-full object-cover" draggable={false} />
          )}
          {loading && <span className="text-xs text-slate-400">loading…</span>}
          {failed && !loading && <span className="px-2 text-center text-[10px] leading-tight text-danger">Could not load — press refresh</span>}
        </div>

        <button
          type="button"
          onClick={() => void load()}
          aria-label="Show me a different code"
          title="Show me a different code"
          className="grid w-10 shrink-0 place-items-center rounded-lg border border-slate-300 text-slate-500 transition hover:border-primary hover:text-primary"
        >
          <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
        </button>

        <input
          id="login-captcha"
          value={value.captchaAnswer}
          onChange={(e) => onChange({ ...value, captchaAnswer: e.target.value })}
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          maxLength={8}
          placeholder="Type the code"
          className="flex-1 rounded-lg border border-slate-300 px-3 py-2 font-mono uppercase tracking-[0.25em] outline-none transition placeholder:font-sans placeholder:normal-case placeholder:tracking-normal focus:border-primary focus:ring-2 focus:ring-primary/20"
        />
      </div>

      <p className="mt-1 text-xs text-slate-400">Capital letters do not matter.</p>
    </div>
  );
}
